import { Box, Grid, Radio, Stack, Typography, useTheme } from '@mui/material';
import {first, get, isEmpty, map, orderBy, truncate} from 'lodash';
import React, { useEffect } from 'react';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import CopyText from 'src/components/common/CopyText';
import { AI_HS_CODE_LABELING_IMAGES } from 'src/utils/constants/imagesUrl';
import { useSingleHSCodePredictionStore } from 'src/zustand/useAILabelingStore';
import numeral from "numeral";

const ListPrediction = ({
  onSelect,
  selected,
}: {
  onSelect: (item?: TypeSingleHSCodePrediction) => void;
  selected?: TypeSingleHSCodePrediction;
}) => {
  const {
    palette: { divider, primary, background },
    shape: { borderRadius },
  } = useTheme();
  const { dispatch, singlePredictions } = useSingleHSCodePredictionStore();

  const matchGain = () => dispatch(true, 'visiblePopupMatchAgain');

  const singlePredictionsSorted = orderBy(singlePredictions.data, ({ confidence }) => confidence, ['desc']);

  useEffect(() => {
    if (!isEmpty(singlePredictionsSorted)) {
      onSelect(first(singlePredictionsSorted));
    }
  }, []);

  return (
    <>
      <Typography mb={2} variant="body2">
        We found{' '}
        <Typography variant="subtitle2" component="span">
          {numeral(singlePredictionsSorted.length || 0).format('0,0')} results
        </Typography>{' '}
        matching your search conditions
      </Typography>
      <Grid container spacing={2}>
        {map(singlePredictionsSorted, (item: TypeSingleHSCodePrediction) => {
          const { hs_code, description } = item;
          const isSelected = hs_code === selected?.hs_code;
          return (
            <Grid item xs={6}>
              <Box
                bgcolor={background.default}
                onClick={() => onSelect(item)}
                height={'100%'}
                sx={{
                  cursor: 'pointer',
                  ':hover': {
                    borderColor: primary.main,
                  },
                }}
                component={'div'}
                alignItems={'start'}
                borderRadius={borderRadius / 2}
                border={'1px solid'}
                borderColor={isSelected ? primary.main : divider}
                display={'flex'}
                gap={2}
                p={2}
              >
                <Radio color="secondary" checked={isSelected} sx={{ padding: 0 }} />

                <Stack spacing={1}>
                  <Stack alignItems={'stretch'} direction={'row'} justifyContent={'space-between'}>
                    <Typography color={'secondary.main'} variant="subtitle1">
                      {hs_code}
                      <CopyText text={hs_code} />
                    </Typography>

                    <Box flex={1} />
                  </Stack>

                  <Typography color={'text.secondary'} variant="body2">
                    {truncate(description, {
                      length: 200,
                    })}
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          );
        })}

        <Grid item xs={6}>
          <Box
            color={background.default}
            height={'100%'}
            component={'div'}
            sx={{
              background: `url(${AI_HS_CODE_LABELING_IMAGES.single_prediction_cover})`,
              backgroundSize: 'cover',
            }}
            gap={2}
            p={2}
            borderRadius={borderRadius / 2}
          >
            <Typography color={'inherit'} variant="subtitle1">
              HS Code Mismatch
            </Typography>
            <Typography color={'text.secondary'} variant="body2">
              Enhance matching <br /> with advanced product details.
            </Typography>
            <ButtonWithLoading onClick={matchGain} sx={{ mt: 2 }} isRounded variant="outlined" color="inheritWhite">
              Match again
            </ButtonWithLoading>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ListPrediction;
